import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import friendService from '../../services/friendService';
import { REELS_CHANGED } from '../stories/reelRing';
import { RING } from '../wall/wallTokens';

const NEW_TAG = 'NEW IN TOWN';

/** The line under a face: who connects you, else how connected they are, else what they do here. */
export function personLine(p) {
  const isNew = p.tag === NEW_TAG;
  if (p.mutualCount > 0) return `${p.mutualCount} MUTUAL${isNew ? ' · NEW' : ''}`;
  if (p.friendCount > 0) return `${p.friendCount} ${p.friendCount === 1 ? 'FRIEND' : 'FRIENDS'}`;
  return p.tag || '';
}

/**
 * PEOPLE YOU MAY KNOW on Home: 4 across on phones, 6 on desktop. + sends a request and takes their
 * introduction off the rail; tapping again takes the request back. A face opens the introduction
 * when they have one up, else their page.
 */
export default function PeopleYouMayKnowGrid({ city, phone, introducedIds = [], onOpenStory }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [sent, setSent] = useState({}); // userId → request id, or true while it is going
  const [issue, setIssue] = useState(null);
  const undoWhenSent = useRef(new Set()); // tapped again before the request had landed

  useEffect(() => {
    let on = true;
    friendService.getPeopleYouMayKnow(12, city || null).then((r) => { if (on && r.success) setData(r.data); });
    return () => { on = false; };
  }, [city]);

  const railChanged = () => window.dispatchEvent(new Event(REELS_CHANGED));

  const undo = async (userId, requestId) => {
    setSent(({ [userId]: _, ...rest }) => rest);
    const r = await friendService.cancelRequest(requestId);
    if (r.success) railChanged();
    else { setSent((m) => ({ ...m, [userId]: requestId })); setIssue('That request could not be taken back. Try again.'); }
  };

  const toggle = async (p) => {
    setIssue(null);
    const held = sent[p.userId];
    if (held === true) { undoWhenSent.current.add(p.userId); setSent(({ [p.userId]: _, ...rest }) => rest); return; }
    if (held) { undo(p.userId, held); return; }
    undoWhenSent.current.delete(p.userId);
    setSent((m) => ({ ...m, [p.userId]: true }));
    const r = await friendService.sendFriendRequest(p.userId);
    if (!r.success) { setSent(({ [p.userId]: _, ...rest }) => rest); setIssue(r.error || 'That request did not go through. Try again.'); return; }
    if (undoWhenSent.current.delete(p.userId)) { undo(p.userId, r.data?.id); return; }
    setSent((m) => ({ ...m, [p.userId]: r.data?.id }));
    railChanged();
  };

  const view = (p, e) => {
    if (introducedIds.includes(p.userId) && onOpenStory?.(p.userId, e.currentTarget.getBoundingClientRect())) return;
    navigate(p.handle ? `/${p.handle}` : `/users/${p.userId}`);
  };

  const people = data?.people || [];
  if (!people.length) return null;

  return (
    <section className="flex flex-col" style={{ gap: 14 }} aria-label="People you may know">
      <div className="flex flex-col" style={{ gap: 6 }}>
        <div className="flex items-center" style={{ gap: 14 }}>
          <span className="whitespace-nowrap font-bold" style={{ fontSize: 14, letterSpacing: '0.14em' }}>PEOPLE YOU MAY KNOW · {data.total}</span>
          <span className="flex-1" style={{ height: 2, background: '#000' }} />
          <Link to="/people?tab=suggested" className="whitespace-nowrap font-bold text-black hover:text-[#FF00AA]" style={{ fontSize: 11, letterSpacing: '0.14em' }}>ALL →</Link>
        </div>
        <span style={{ fontSize: 10, lineHeight: 1.5, letterSpacing: '.06em', color: '#6B7280' }}>Friends of your friends first, then the most connected in {city || 'town'}. + adds them.</span>
      </div>
      {issue && <span role="alert" style={{ fontSize: 11, color: '#B91C1C' }}>{issue}</span>}
      <div className="grid" style={{ gridTemplateColumns: `repeat(${phone ? 4 : 6},minmax(0,1fr))`, gap: '18px 10px' }}>
        {people.map((p) => {
          const added = !!sent[p.userId];
          const name = p.displayName.trim().split(' ')[0].toUpperCase();
          return (
            <div key={p.userId} data-testid="may-know" className="flex min-w-0 flex-col items-center" style={{ gap: 6 }}>
              <span className="relative block w-full" style={{ maxWidth: 84, aspectRatio: '1' }}>
                <button type="button" onClick={(e) => view(p, e)} aria-label={`View ${p.displayName}`} className="absolute inset-0 rounded-full" style={{ padding: 2, background: RING }}>
                  <span className="flex h-full w-full items-center justify-center overflow-hidden rounded-full font-bold" style={{ border: '2px solid #F5F5F5', background: '#E5E7EB', fontSize: 15, boxSizing: 'border-box' }}>
                    {p.avatarUrl ? <img src={p.avatarUrl} alt="" className="block h-full w-full object-cover" referrerPolicy="no-referrer" /> : name.charAt(0)}
                  </span>
                </button>
                {p.tag === NEW_TAG && <span className="absolute flex items-center rounded-full font-bold text-black" style={{ left: '50%', top: -8, transform: 'translateX(-50%)', height: 16, padding: '0 7px', background: '#22ECFB', fontSize: 8, letterSpacing: '.14em' }}>NEW</span>}
                <button
                  type="button"
                  onClick={() => toggle(p)}
                  aria-label={added ? `Undo add ${p.displayName}` : `Add ${p.displayName}`}
                  aria-pressed={added}
                  className="absolute flex items-center justify-center rounded-full"
                  style={{ right: -2, bottom: -2, width: 28, height: 28, padding: 0, border: '2px solid #F5F5F5', background: added ? '#22ECFB' : '#000', color: added ? '#000' : '#fff' }}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={added ? 4 : 3.5} strokeLinecap="square" aria-hidden>
                    <path d={added ? 'M4 12l5 5L20 6' : 'M12 5v14M5 12h14'} />
                  </svg>
                </button>
              </span>
              <span className="max-w-full truncate font-bold" style={{ fontSize: 11, letterSpacing: '.12em' }}>{name}</span>
              <span className="max-w-full truncate text-center font-bold" style={{ fontSize: 8, letterSpacing: '.1em', color: added ? '#0E9AA7' : '#6B7280' }}>{added ? 'REQUEST SENT' : personLine(p)}</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}
